import { action, computed, makeObservable, observable } from 'mobx'
import { Cubit } from '../core'
import type { MeetingCubit } from '../MeetingCubit'
import { ServerIdentities } from '../../types'
import { z } from 'zod'
import { SlideQuestionType } from '../../models/SlideQuestionType'

export enum ModerationTaskStatus {
  active = 'active',
  pending = 'pending',
  completed = 'completed',
}

export class ModerationTasksController extends Cubit {
  cubit: MeetingCubit

  @observable
  lastAttributeValue: string | null = null

  @observable
  showDisplay: boolean = false

  overrides = observable.map<
    string,
    {
      taskCompleted: boolean
      taskActive: boolean
    }
  >()

  constructor(
    cubit: MeetingCubit,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    options: Record<string, never> = {}
  ) {
    super()
    this.cubit = cubit

    makeObservable(this)
  }

  initialize(): void {
    if (!this.cubit.livekitRoom) return

    this.cubit.livekitRoom.on(
      'participantAttributesChanged',
      this.handleAttributesChange
    )

    this.copyModeratorAttributes()
  }

  getModeratorAttributes() {
    if (!this.cubit.livekitRoom) return {}
    const moderator = this.cubit.livekitRoom.getParticipantByIdentity(
      ServerIdentities.Moderator
    )
    if (!moderator) return {}
    return moderator.attributes || {}
  }

  handleAttributesChange = (
    _: Record<string, string>,
    participant: { identity: string }
  ) => {
    // only interested in moderator attributes
    if (participant.identity !== ServerIdentities.Moderator) return
    this.copyModeratorAttributes()
  }

  @action
  copyModeratorAttributes(): void {
    const attributes = this.getModeratorAttributes()
    const newValue = attributes.moderationTasks || null
    if (this.lastAttributeValue === newValue) return
    this.lastAttributeValue = attributes.moderationTasks || null
  }

  dispose(): symbol {
    if (this.cubit.livekitRoom) {
      this.cubit.livekitRoom.off(
        'participantAttributesChanged',
        this.handleAttributesChange
      )
    }
    return super.dispose()
  }

  @computed
  get moderationTasks() {
    if (!this.displayModerationTasksSettingEnabled) return null

    if (this.cubit.isPreview) {
      return this.installOverrides(this.previewModerationTasks)
    }

    if (!this.lastAttributeValue) return null

    const json = JSON.parse(this.lastAttributeValue)

    const parsed = moderationTasksSchema.safeParse(json)

    if (!parsed.success) {
      console.error('Failed to parse moderation tasks', parsed.error)
      return null
    }

    return this.installOverrides(parsed.data)
  }

  @computed
  get displayModerationTasksSettingEnabled() {
    return (
      this.cubit.slideDeckQuestions.models
        .filter((q) => q.data.slideId === this.cubit.currentSlideId)
        .filter(
          (q) => q.data.questionType === SlideQuestionType.displayModerationTask
        ).length > 0
    )
  }

  installOverrides(data: Record<string, ServerModerationTask> | null) {
    if (!data) return null

    const newData = { ...data }
    if (this.overrides) {
      const keys = Array.from(this.overrides.keys())
      for (const taskId of keys) {
        const override = this.overrides.get(taskId)
        const task = newData[taskId]
        if (task && override) {
          newData[taskId] = {
            ...task,
            taskCompleted: override.taskCompleted,
            taskActive: override.taskActive,
          }
        }
      }
    }

    return newData
  }

  @computed
  get previewModerationTasks() {
    const slide = this.cubit.currentSlide
    if (!slide) return null

    const previewTasks: Record<string, ServerModerationTask> = {}
    slide.moderationTasks.map((task, index) => {
      previewTasks[task.id] = {
        slideId: task.data.slideId || '',
        slideQuestionId: task.id || '',
        summary: task.data.question || null,
        taskActive: index === 0, // first task is active
        taskCompleted: false,
        taskCompletedTime: null,
      }
    })

    return previewTasks
  }

  @computed
  get allDone() {
    const tasks = this.moderationTasks
    return (
      tasks && Object.values(tasks).every((task) => task.taskCompleted === true)
    )
  }

  @computed
  get activeId() {
    const tasks = this.moderationTasks
    // if moderation tasks are available, use them
    if (tasks) {
      const entries = Object.entries(tasks)

      const activeTask = entries.find(([, task]) => {
        return task.taskActive
      })

      if (activeTask) return activeTask[0]
    }

    return undefined
  }

  @computed
  get activeIndex() {
    const slide = this.cubit.currentSlide
    if (!slide) return 0

    return (
      slide.moderationTasks.findIndex((task) => task.id === this.activeId) || 0
    )
  }

  @action
  addOverride(taskId: string, taskCompleted: boolean, taskActive: boolean) {
    this.overrides.set(taskId, { taskCompleted, taskActive })
  }

  @action
  setOverrides(
    overrides: Record<string, { taskCompleted: boolean; taskActive: boolean }>
  ) {
    Object.entries(overrides).forEach(
      ([taskId, { taskCompleted, taskActive }]) => {
        this.overrides.set(taskId, { taskCompleted, taskActive })
      }
    )
  }

  @action
  removeOverride(taskId: string) {
    this.overrides.delete(taskId)
  }

  @action
  clearOverrides() {
    this.overrides.clear()
  }

  @action
  setShowDisplay(value: boolean) {
    this.showDisplay = value
  }
}

const moderationTaskSchema = z.object({
  slideId: z.string(),
  slideQuestionId: z.string(),
  summary: z.string().nullable(),
  taskActive: z.boolean(),
  taskCompleted: z.boolean(),
  taskCompletedTime: z.string().nullable(),
})
const moderationTasksSchema = z.record(z.string(), moderationTaskSchema)

type ServerModerationTask = z.infer<typeof moderationTaskSchema>
